import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { Customization, DraftOrder, DraftOrderLine } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useCustomizations, useSizeSets } from '@/features/dictionary';
import { useProduct } from '@/features/products';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { fromCatalogLine, toCatalogLineBody } from '../api/mappers';
import { useUpdateDraftLine } from '../hooks/use-draft-order-mutations';
import { customizationsFor, keptFilesOf } from '../schemas/customization-rules';
import { editLineFieldPath, isStateConflict } from '../schemas/draft-order-rules';
import {
  lineEditSchema,
  type LineEditFormInput,
  type LineEditFormOutput,
} from '../schemas/line-schemas';

import { CustomizationField } from './customization-field';

export interface EditLineDialogProps {
  storeId: string;
  draft: DraftOrder;
  line: DraftOrderLine;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The product's customization definitions; the form waits for them so its schema is complete. */
function useLineDefinitions(line: DraftOrderLine) {
  const product = useProduct(line.productId ?? '');
  const dictionary = useCustomizations();
  const sizeSets = useSizeSets();
  const defs = useMemo(
    () =>
      customizationsFor(
        product.data?.customizationIds ?? [],
        dictionary.data ?? [],
        sizeSets.data ?? [],
      ),
    [product.data, dictionary.data, sizeSets.data],
  );
  const ready = product.isSuccess && dictionary.isSuccess && sizeSets.isSuccess;
  const failed = product.isError || dictionary.isError || sizeSets.isError;
  return { defs, ready, failed };
}

/**
 * Changes one catalog line in place (PATCH .../lines/:lineId): quantity, a price override and the
 * personalisation, files included. The variant stays; to switch it, remove the line and add the
 * other one.
 */
export function EditLineDialog({ storeId, draft, line, open, onOpenChange }: EditLineDialogProps) {
  const { t } = useTranslation(['draftOrders', 'common']);
  const { defs, ready, failed } = useLineDefinitions(line);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('draftOrders:editLine.title', { title: line.title })}</DialogTitle>
          <DialogDescription>
            {line.variantTitle
              ? t('draftOrders:editLine.descriptionVariant', { variant: line.variantTitle })
              : t('draftOrders:editLine.description')}
          </DialogDescription>
        </DialogHeader>
        {failed ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{t('draftOrders:editLine.definitionsFailed')}</AlertDescription>
          </Alert>
        ) : !ready ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : (
          <EditLineForm
            storeId={storeId}
            draft={draft}
            line={line}
            defs={defs}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditLineForm({
  storeId,
  draft,
  line,
  defs,
  onDone,
}: {
  storeId: string;
  draft: DraftOrder;
  line: DraftOrderLine;
  defs: Customization[];
  onDone: () => void;
}) {
  const { t } = useTranslation(['draftOrders', 'common']);
  const format = useFormat();
  const message = useErrorMessage();
  const update = useUpdateDraftLine(storeId, 'draftOrders:lines.saved');
  const schema = useMemo(() => lineEditSchema(defs), [defs]);
  const form = useForm<LineEditFormInput, unknown, LineEditFormOutput>({
    resolver: useI18nResolver(schema),
    defaultValues: fromCatalogLine(line, defs),
  });
  const overridePrice = form.watch('overridePrice');
  const kept = keptFilesOf(line.customizations);
  const position = draft.lines.findIndex((l) => l.id === line.id);

  const submit = form.handleSubmit(async (values) => {
    const body = toCatalogLineBody(values, defs, kept);
    const keys = (body.customizations ?? []).map((c) => c.key);
    try {
      await update.mutateAsync({ id: draft.id, lineId: line.id, body });
      onDone();
    } catch (error) {
      const applied = applyFieldErrors(error, form.setError, {
        knownFields: new Set([
          'quantity',
          'unitPrice',
          ...defs.map((d) => `customizations.${d.key}`),
        ]),
        mapField: (field) => editLineFieldPath(field, position, keys) ?? 'root.server',
      });
      if (applied) return;
      form.setError('root.server', {
        type: 'server',
        message: isStateConflict(error) ? t('draftOrders:errors.locked') : message(error),
      });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="quantity"
          render={({ field }) => (
            <FormItem className="max-w-32">
              <FormLabel>{t('draftOrders:picker.quantity')}</FormLabel>
              <FormControl>
                <Input type="number" inputMode="numeric" min={1} max={99} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {defs.length > 0 ? (
          <fieldset className="flex flex-col gap-3">
            <legend className="mb-2 text-sm font-medium">
              {t('draftOrders:picker.customizations')}
            </legend>
            {defs.map((def) => (
              <FormField
                key={def.id}
                control={form.control}
                name={`customizations.${def.key}`}
                render={({ field, fieldState }) => (
                  <CustomizationField
                    def={def}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    inputRef={field.ref}
                    invalid={fieldState.invalid}
                    kept={line.customizations.find((c) => c.key === def.key)?.file ?? null}
                  />
                )}
              />
            ))}
          </fieldset>
        ) : null}
        <FormField
          control={form.control}
          name="overridePrice"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start justify-between gap-4 rounded-md border p-3">
              <div className="space-y-0.5">
                <FormLabel>{t('draftOrders:picker.overridePrice')}</FormLabel>
                {!line.priceOverridden ? (
                  <FormDescription>
                    {t('draftOrders:picker.overridePriceHint', {
                      price: format.money(line.unitPrice),
                    })}
                  </FormDescription>
                ) : (
                  <FormDescription>{t('draftOrders:editLine.overrideOffHint')}</FormDescription>
                )}
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    if (!checked) {
                      form.setValue('unitPrice', null);
                      form.clearErrors('unitPrice');
                    }
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        {overridePrice ? (
          <FormField
            control={form.control}
            name="unitPrice"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('draftOrders:picker.unitPrice')}</FormLabel>
                <FormControl>
                  <MoneyInput
                    currency={draft.currency}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    aria-invalid={fieldState.invalid}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onDone}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? t('draftOrders:editLine.saving') : t('draftOrders:editLine.save')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
